import { combineEpics, Epic } from 'redux-observable'
import { catchError, concat, of, tap } from 'rxjs'

import { formatError } from '../helpers/formatError'
import { logError } from './ws/actions'

export const MAX_CONSECUTIVE_EPIC_FAILURES = 5

/*
  Stopgap until every epic has its own catchError + logError: isolate each
  epic so an unhandled error can't kill the combineEpics root, resubscribing
  it via `caught`. The consecutive-failure cap (reset on emission) bounds
  the resubscribe loop of an epic that errors at subscription time.
*/
export const safeCombineEpics =
  (...epics: Epic[]): Epic =>
  (action$, state$, deps) =>
    combineEpics(
      ...epics.map(
        (epic): Epic =>
          (a$, s$, d) => {
            let consecutiveFailures = 0
            return epic(a$, s$, d).pipe(
              tap(() => (consecutiveFailures = 0)),
              catchError((err, caught) => {
                consecutiveFailures += 1
                const message = formatError(err)
                if (consecutiveFailures >= MAX_CONSECUTIVE_EPIC_FAILURES) {
                  return of(
                    logError(
                      `Epic disabled after ${consecutiveFailures} consecutive errors: ${message}`
                    )
                  )
                }
                return concat(of(logError(message)), caught)
              })
            )
          }
      )
    )(action$, state$, deps)
